import Swiper from 'swiper'
import { Navigation, Pagination, Autoplay } from 'swiper/modules'

// Usage:
// .swiper <id> <slidePerView (swiper-slide-auto/swiper-slide-md-2)> <autoplay (swiper-autoplay or swiper-autoplay-5000)> <direction (swiper-vertical)> <click-to-next (swiper-click-next)>
// .swiper#swiper-home-committee.swiper-slide-auto
// .swiper#swiper-discover.swiper-slide-1.swiper-autoplay
// .swiper#swiper-travel-info.swiper-vertical.swiper-click-next.swiper-autoplay-3000

const BREAKPOINT_MAP = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}

const getBreakpointValue = (breakpoint) => BREAKPOINT_MAP[breakpoint] || null

const parseSwiperClasses = (container) => {
  let slidesPerView = 1 // Default value
  let autoplayDelay = null // Default: no autoplay
  let direction = 'horizontal' // Default direction
  let clickToNext = false // Default: no click to next
  const breakpoints = {}

  Array.from(container.classList).forEach((className) => {
    // Handle auto slides
    if (className === 'swiper-slide-auto') {
      slidesPerView = 'auto'
      return
    }

    // Handle vertical direction
    if (className === 'swiper-vertical') {
      direction = 'vertical'
      return
    }

    // Handle click to next
    if (className === 'swiper-click-next') {
      clickToNext = true
      return
    }

    // Handle autoplay
    if (className === 'swiper-autoplay') {
      autoplayDelay = 3000 // Default delay
      return
    }

    // Handle autoplay with custom delay (e.g., swiper-autoplay-5000)
    if (className.startsWith('swiper-autoplay-')) {
      const delay = parseInt(className.split('-')[2], 10)
      if (!isNaN(delay)) {
        autoplayDelay = delay
      }
      return
    }

    // Skip non-swiper classes
    if (!className.startsWith('swiper-slide-')) return

    const parts = className.split('-')

    if (parts.length === 3) {
      // Format: swiper-slide-N (e.g., swiper-slide-2)
      const raw = parts[2].replace('_', '.')
      const num = parseFloat(raw)
      if (!Number.isNaN(num)) {
        slidesPerView = num
      }
    } else if (parts.length === 4) {
      // Format: swiper-slide-BP-N (e.g., swiper-slide-md-4)
      const breakpoint = parts[2]
      const raw = parts[3].replace('_', '.')
      const num = parseFloat(raw)

      if (!Number.isNaN(num)) {
        const breakpointValue = getBreakpointValue(breakpoint)
        if (breakpointValue) {
          breakpoints[breakpointValue] = { slidesPerView: num }
        }
      }
    }
  })

  return { slidesPerView, breakpoints, autoplayDelay, direction, clickToNext }
}

const createBaseConfig = (swiperId) => ({
  modules: [Navigation, Pagination, Autoplay],
  spaceBetween: 25,
  loop: true,
  pagination: {
    el: `#${swiperId} .swiper-pagination`,
    clickable: true,
  },
  navigation: {
    nextEl: `#${swiperId} .swiper-button-next`,
    prevEl: `#${swiperId} .swiper-button-prev`,
  },
})

const initSwipers = () => {
  const swipers = []

  // Find all elements with IDs starting with "swiper-"
  document.querySelectorAll('[id^="swiper-"]').forEach((container) => {
    const swiperId = container.id
    const {
      slidesPerView,
      breakpoints,
      autoplayDelay,
      direction,
      clickToNext,
    } = parseSwiperClasses(container)

    // Create base configuration
    const config = createBaseConfig(swiperId)

    // Add direction if vertical
    if (direction === 'vertical') {
      config.direction = 'vertical'
      config.speed = 400
      config.spaceBetween = 0
    }

    // Add slidesPerView if it's not the default
    if (slidesPerView !== 1) {
      config.slidesPerView = slidesPerView
    }

    // Add breakpoints if any were found
    if (Object.keys(breakpoints).length > 0) {
      config.breakpoints = breakpoints
    }

    // Add autoplay if enabled
    if (autoplayDelay !== null) {
      config.autoplay = {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }
    }

    // Initialize Swiper
    const swiper = new Swiper(`#${swiperId}`, config)

    // Add click to next functionality if enabled
    if (clickToNext) {
      swiper.on('click', () => {
        swiper.slideNext()
      })
    }

    swipers.push({
      id: swiperId,
      swiper,
      element: container,
    })
  })

  return swipers
}

export default initSwipers
